import React, { useState, useEffect, Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, Plus, Search } from 'lucide-react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import { toast } from 'sonner';
import TaskTableView from '../components/ui/TaskTableView';
import { FiGrid, FiList } from 'react-icons/fi';
import ProblemCard from '../components/ProblemCard';
import ProblemDetailModal from '../components/ProblemDetailModal';
import ProblemFormModal from '../components/ProblemFormModal';
import BadgeDropdown from '../components/ui/BadgeDropdown';
import { fetchProblems, createProblem, updateProblem, deleteProblem, saveSolutions, debugSolutionsRLS } from '../utils/supabaseHelpers';

const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [problems, setProblems] = useState([]);
  const [filteredProblems, setFilteredProblems] = useState([]);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingProblem, setEditingProblem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [filters, setFilters] = useState({
    domain: 'all',
    status: 'all',
    priority: 'all'
  });

  // -- Add these lines --
  const sortOptions = [
    { id: 'created_at-desc', name: 'Newest', value: 'Sort: Newest' },
    { id: 'created_at-asc', name: 'Oldest', value: 'Sort: Oldest' },
    { id: 'updated_at-desc', name: 'Updated', value: 'Sort: Recently Updated' },
    { id: 'title-asc', name: 'Title A-Z', value: 'Sort: Title A-Z' },
    { id: 'title-desc', name: 'Title Z-A', value: 'Sort: Title Z-A' },
  ];
  const [selectedSort, setSelectedSort] = useState(sortOptions[0]);
  // -- End of new lines --

  useEffect(() => {
    loadProblems();
  }, []);

  // Handle navigation from Assignments page to auto-open problem modal
  useEffect(() => {
    const problemToOpenId = location.state?.openProblemId;
    if (problemToOpenId) {
      // 1. Find the problem from the full list
      const problemToOpen = problems.find(p => p.id === problemToOpenId);
      if (problemToOpen) {
        // 2. Open the problem detail modal
        setSelectedProblem(problemToOpen);
        setShowDetailModal(true);
        
        // 3. Clear the state to prevent re-opening
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, problems, navigate]);

  const loadProblems = async (sortOption = null) => {
    try {
      setLoading(true);
      const sortToUse = sortOption || selectedSort.value;
      const data = await fetchProblems(sortToUse);
      setProblems(data);
    } catch (error) {
      toast.error('Failed to load problems: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to refresh a specific problem with solutions
  const refreshProblemWithSolutions = async (problemId) => {
    try {
      const refreshedProblems = await fetchProblems(selectedSort.value);
      const refreshedProblem = refreshedProblems.find(p => p.id === problemId);
      if (refreshedProblem && selectedProblem?.id === problemId) {
        setSelectedProblem(refreshedProblem);
        console.log('✅ Dashboard: Selected problem refreshed with solutions:', refreshedProblem.solutions?.length || 0);
      }
      return refreshedProblem;
    } catch (error) {
      console.error('❌ Dashboard: Error refreshing problem:', error);
      return null;
    }
  };

  // Reload data when sort changes
  useEffect(() => {
    if (selectedSort) {
      loadProblems(selectedSort.value);
    }
  }, [selectedSort]);

  // Helper function to map simplified status categories to database values
  const getStatusMapping = (category) => {
    switch (category) {
      case 'done':
        return ['Done', 'Solved', 'completed'];
      case 'in_progress':
        return ['In Progress', 'pending'];
      case 'not_decided':
        // This is the default case - includes all other statuses
        return ['Not Decided', 'decided_', 'new', 'under_discussion', 'solution_decided'];
      default:
        return [];
    }
  };

  useEffect(() => {
    let filtered = problems;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Domain filter
    if (filters.domain !== 'all') {
      filtered = filtered.filter(p => p.domain?.toLowerCase() === filters.domain.toLowerCase());
    }

    // Status filter - now uses simplified categories with mapping
    if (filters.status !== 'all') {
      const statusValues = getStatusMapping(filters.status);
      filtered = filtered.filter(p => {
        if (filters.status === 'not_decided') {
          // For "not_decided", include anything that's NOT in the other categories
          const doneStatuses = getStatusMapping('done');
          const inProgressStatuses = getStatusMapping('in_progress');
          const excludedStatuses = [...doneStatuses, ...inProgressStatuses];
          return !excludedStatuses.includes(p.status);
        } else {
          // For other categories, check if status is in the mapped values
          return statusValues.includes(p.status);
        }
      });
    }

    // Priority filter
    if (filters.priority !== 'all') {
      filtered = filtered.filter(p => p.priority?.toLowerCase() === filters.priority.toLowerCase());
    }

    // Sorting is now handled at the database level, no need for frontend sorting

    setFilteredProblems(filtered);
  }, [problems, searchTerm, filters]);

  const handleCreateProblem = async (problemData) => {
    try {
      console.log('=== DASHBOARD CREATE STARTED ===');
      console.log('➕ Dashboard: Creating new problem with data:', problemData);
      console.log('➕ Dashboard: Solutions to save:', problemData.solutions);
      console.log('➕ Dashboard: Solutions count:', problemData.solutions?.length || 0);
      
      // Step 1: Create the problem record
      console.log('📝 Dashboard: Creating problem record...');
      const newProblem = await createProblem({
        title: problemData.title,
        description: problemData.description,
        domain: problemData.domain,
        priority: problemData.priority,
        status: problemData.status,
        due_date: problemData.dueDate || null,
        notes: problemData.notes || ''
        // Removed solutions - it's a separate table, not a column
      });
      
      console.log('✅ Dashboard: Problem created successfully:', newProblem);
      console.log('✅ Dashboard: New problem ID:', newProblem.id);
      
      // Step 2: Save solutions to separate table (with error handling)
      if (problemData.solutions && problemData.solutions.length > 0) {
        console.log('💾 Dashboard: About to call saveSolutions for new problem...');
        console.log('💾 Dashboard: Problem ID:', newProblem.id);
        console.log('💾 Dashboard: Solutions data:', problemData.solutions);
        
        try {
          const result = await saveSolutions(newProblem.id, problemData.solutions);
          console.log('✅ Dashboard: saveSolutions completed for new problem, returned:', result);
          console.log('✅ Dashboard: Number of solutions saved:', result?.data?.length || 0);
        } catch (solutionsError) {
          console.error('❌ Dashboard: Error saving solutions:', solutionsError);
          console.error('❌ Dashboard: This is a blocking error - solutions must be saved properly');
          throw solutionsError; // Re-throw to prevent incomplete saves
        }
      } else {
        console.log('ℹ️ Dashboard: No solutions to save for new problem');
      }
      
      // Step 3: Refetch all problems to get the new problem with solutions
      console.log('🔄 Dashboard: Refetching problems list to include new problem with solutions...');
      await loadProblems(selectedSort.value);
      
      toast.success('Problem created successfully!');
      setShowFormModal(false);
      
      console.log('=== DASHBOARD CREATE COMPLETED SUCCESSFULLY ===');
    } catch (error) {
      console.error('=== DASHBOARD CREATE ERROR ===');
      console.error('❌ Dashboard: Failed to create problem:', error);
      console.error('❌ Dashboard: Error details:', {
        message: error.message,
        stack: error.stack,
        solutionsCount: problemData?.solutions?.length
      });
      
      // If it's a solutions-related error, run RLS debugging
      if (error.message?.includes('solutions') || error.message?.includes('delete') || error.message?.includes('RLS') || error.message?.includes('policy')) {
        console.log('🔍 Running RLS debugging due to solutions error...');
        await debugSolutionsRLS();
      }
      
      // If it's a function not found error, provide guidance
      if (error.message?.includes('function') && error.message?.includes('does not exist')) {
        console.error('🚨 SETUP REQUIRED: Please create the delete function in Supabase');
        console.error('🚨 Run the SQL commands in debug-rls-policies.sql in your Supabase SQL Editor');
        toast.error('Database function missing. Please check console for setup instructions.');
      } else {
        toast.error('Failed to create problem: ' + error.message);
      }
    }
  };

  const handleUpdateProblem = async (problemData) => {
    try {
      console.log('=== DASHBOARD UPDATE STARTED ===');
      console.log('🔄 Dashboard: Starting problem update for ID:', problemData.id);
      console.log('🔄 Dashboard: Full problem data received:', problemData);
      console.log('🔄 Dashboard: Solutions to save:', problemData.solutions);
      console.log('🔄 Dashboard: Solutions count:', problemData.solutions?.length || 0);
      
      // Step 1: Update the problem record
      console.log('📝 Dashboard: Updating problem record...');
      const updatedProblem = await updateProblem(problemData.id, {
        title: problemData.title,
        description: problemData.description,
        domain: problemData.domain,
        priority: problemData.priority,
        status: problemData.status,
        due_date: problemData.dueDate || null,
        notes: problemData.notes || ''
        // Removed solutions - it's a separate table, not a column
      });
      
      console.log('✅ Dashboard: Problem updated successfully:', updatedProblem);
      
      // Step 2: Save solutions to separate table (with error handling)
      console.log('💾 Dashboard: About to call saveSolutions...');
      console.log('💾 Dashboard: Problem ID:', problemData.id);
      console.log('💾 Dashboard: Solutions data:', problemData.solutions || []);
      
      try {
        const result = await saveSolutions(problemData.id, problemData.solutions || []);
        console.log('✅ Dashboard: saveSolutions completed, returned:', result);
        console.log('✅ Dashboard: Number of solutions returned:', result?.data?.length || 0);
      } catch (solutionsError) {
        console.error('❌ Dashboard: Error saving solutions:', solutionsError);
        console.error('❌ Dashboard: This is a blocking error - solutions must be saved properly');
        throw solutionsError; // Re-throw to prevent incomplete saves
      }
      
      // Step 3: Refetch all problems to get updated solutions
      console.log('🔄 Dashboard: Refetching problems list to get updated solutions...');
      await loadProblems(selectedSort.value);
      
      // Step 4: Update selected problem if it's the one we just updated
      if (selectedProblem?.id === updatedProblem.id) {
        await refreshProblemWithSolutions(updatedProblem.id);
      }
      
      toast.success('Problem updated successfully!');
      setShowFormModal(false);
      setEditingProblem(null);
      
      console.log('=== DASHBOARD UPDATE COMPLETED SUCCESSFULLY ===');
    } catch (error) {
      console.error('=== DASHBOARD UPDATE ERROR ===');
      console.error('❌ Dashboard: Failed to update problem:', error);
      console.error('❌ Dashboard: Error details:', {
        message: error.message,
        stack: error.stack,
        problemId: problemData?.id,
        solutionsCount: problemData?.solutions?.length
      });
      
      // If it's a solutions-related error, run RLS debugging
      if (error.message?.includes('solutions') || error.message?.includes('delete') || error.message?.includes('RLS') || error.message?.includes('policy')) {
        console.log('🔍 Running RLS debugging due to solutions error...');
        await debugSolutionsRLS();
      }
      
      // If it's a function not found error, provide guidance
      if (error.message?.includes('function') && error.message?.includes('does not exist')) {
        console.error('🚨 SETUP REQUIRED: Please create the delete function in Supabase');
        console.error('🚨 Run the SQL commands in debug-rls-policies.sql in your Supabase SQL Editor');
        toast.error('Database function missing. Please check console for setup instructions.');
      } else {
        toast.error('Failed to update problem: ' + error.message);
      }
    }
  };

  const handleDeleteProblem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this problem?')) return;
    
    try {
      await deleteProblem(id);
      setProblems(problems.filter(p => p.id !== id));
      toast.success('Problem deleted successfully');
      setShowDetailModal(false);
      setSelectedProblem(null);
    } catch (error) {
      toast.error('Failed to delete problem: ' + error.message);
    }
  };

  const handleEditClick = (problem) => {
    setEditingProblem(problem);
    setShowFormModal(true);
    setShowDetailModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="loading-dots mb-4">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p className="text-gray-400">Loading problems...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 overflow-visible" data-testid="dashboard-page">
      {/* Search & Filters */}
      <div className="glass-card p-6 pb-8 mb-12 relative z-40 overflow-visible">
        <div className="flex flex-col lg:flex-row gap-4 pb-4 overflow-visible">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search entries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full glass-input"
              style={{ paddingLeft: '45px' }}
              data-testid="search-input"
            />
          </div>

          <BadgeDropdown
            value={filters.domain}
            onChange={(value) => setFilters({ ...filters, domain: value })}
            type="domain"
            showAllOption={true}
            options={[
              { value: 'mechanical', label: 'Mechanical' },
              { value: 'software', label: 'Software' },
              { value: 'electronics', label: 'Electronics' },
              { value: 'operations', label: 'Operations' }
            ]}
            className="min-w-[140px]"
            data-testid="filter-domain"
          />

          <BadgeDropdown
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value })}
            type="status"
            showAllOption={true}
            options={[
              { value: 'not_decided', label: 'Not Decided' },
              { value: 'in_progress', label: 'In Progress' },
              { value: 'done', label: 'Done' }
            ]}
            className="min-w-[160px]"
            data-testid="filter-status"
          />

          <BadgeDropdown
            value={filters.priority}
            onChange={(value) => setFilters({ ...filters, priority: value })}
            type="priority"
            showAllOption={true}
            options={[
              { value: 'critical', label: 'Critical' },
              { value: 'high', label: 'High' },
              { value: 'medium', label: 'Medium' },
              { value: 'low', label: 'Low' }
            ]}
            className="min-w-[120px]"
            data-testid="filter-priority"
          />

          {/* --- Sort By Dropdown --- */}
          <div className="relative z-50">
            <Listbox value={selectedSort} onChange={setSelectedSort}>
              <div className="relative">
              <Listbox.Button className="glass-input cursor-pointer w-full text-left pl-4 pr-12 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all duration-200 hover:border-indigo-400/50 min-w-[140px]">
                <span className="block truncate">{selectedSort.name}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                  <ChevronDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                </span>
              </Listbox.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Listbox.Options 
                  className="absolute mt-1 w-full overflow-visible rounded-lg bg-gray-800 border border-gray-600 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                  style={{ zIndex: 9999 }}
                >
                  {sortOptions.map((option) => (
                    <Listbox.Option
                      key={option.id}
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 pl-10 pr-4 transition-colors duration-150 ${
                          active ? 'bg-indigo-600 text-white' : 'text-gray-200 hover:bg-gray-700'
                        }`
                      }
                      value={option}
                    >
                      {({ selected }) => (
                        <>
                          <span
                            className={`block truncate ${
                              selected ? 'font-medium' : 'font-normal'
                            }`}
                          >
                            {option.name}
                          </span>
                          {selected ? (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-400">
                              <CheckIcon className="h-4 w-4" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
              </div>
            </Listbox>
          </div>

          {/* View Toggle Buttons */}
          <div className="flex p-1 bg-gray-700 rounded-lg">
            <button
              onClick={() => setViewMode('cards')}
              className={`flex items-center px-3 py-1 rounded-md text-sm font-medium ${
                viewMode === 'cards' 
                  ? 'bg-indigo-600 text-white shadow' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <FiGrid className="inline-block mr-2" />
              Cards
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center px-3 py-1 rounded-md text-sm font-medium ${
                viewMode === 'table' 
                  ? 'bg-indigo-600 text-white shadow' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <FiList className="inline-block mr-2" />
              Table
            </button>
          </div>

          <button
            onClick={() => {
              setEditingProblem(null);
              setShowFormModal(true);
            }}
            className="btn-primary flex items-center gap-2 whitespace-nowrap"
            data-testid="create-problem-btn"
          >
            <Plus size={20} />
            Create Task
          </button>
        </div>
      </div>

      {/* === Conditional View === */}
      {viewMode === 'cards' ? (
        // --- CARD VIEW ---
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-6">
          {filteredProblems.map((problem) => (
            <ProblemCard
              key={problem.id}
              problem={problem}
              onClick={() => {
                setSelectedProblem(problem);
                setShowDetailModal(true);
              }}
            />
          ))}
        </div>
      ) : (
        // --- TABLE VIEW ---
        <TaskTableView problems={filteredProblems} />
      )}
      {/* === End Conditional View === */}

      {filteredProblems.length === 0 && !loading && (
        <div className="glass-card p-12 text-center" data-testid="no-problems">
          <AlertCircle className="mx-auto mb-4 text-gray-400" size={48} />
          <p className="text-gray-400 text-lg mb-4">
            {problems.length === 0 ? 'No problems yet. Create your first problem!' : 'No problems found matching your criteria'}
          </p>
          {problems.length === 0 && (
            <button
              onClick={() => setShowFormModal(true)}
              className="btn-primary"
            >
              Create Task
            </button>
          )}
        </div>
      )}

      {filteredProblems.length > 0 && (
        <div className="text-center text-gray-400" data-testid="problem-count">
          Showing {filteredProblems.length} of {problems.length} problems
        </div>
      )}

      {/* Modals */}
      {showDetailModal && selectedProblem && (
        <ProblemDetailModal
          problem={selectedProblem}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedProblem(null);
          }}
          onEdit={() => handleEditClick(selectedProblem)}
          onDelete={() => handleDeleteProblem(selectedProblem.id)}
          onUpdate={async (updatedProblem) => {
            try {
              const updated = await updateProblem(updatedProblem.id, {
                status: updatedProblem.status
              });
              
              // Refetch all problems to ensure we have the latest data with solutions
              console.log('🔄 Dashboard: Refetching problems after status update...');
              await loadProblems(selectedSort.value);
              
              // Update selected problem with fresh data including solutions
              await refreshProblemWithSolutions(updated.id);
              
              toast.success('Status updated');
            } catch (error) {
              toast.error('Failed to update: ' + error.message);
            }
          }}
        />
      )}

      {showFormModal && (
        <ProblemFormModal
          problem={editingProblem}
          onClose={() => {
            setShowFormModal(false);
            setEditingProblem(null);
          }}
          onSubmit={editingProblem ? handleUpdateProblem : handleCreateProblem}
        />
      )}
    </div>
  );
};

export default Dashboard;
